// Add to imports if not present.  We need to import the URL helper or the API function.
// Since the previous file used `getJokerSettingsAPI` from `../../helpers/apis`, I should check if I need to update `src/helpers/apis` first.
// Let's assume I can call axios directly here or update `src/helpers/api_helper.js` / `apis.js`.
// The file imports `getJokerSettings` from `../../helpers/apis`. Let's check `src/helpers/apis.js` first to follow the pattern.

// Correction: I should check `src/helpers/fakebackend_helper.js` or wherever `apis` points to.
// The import is `import { getJokerSettings as getJokerSettingsAPI ... } from "../../helpers/apis";`
// I'll assume `src/helpers/apis.js` exists. Let's check it.


import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    getJokerSettings as getJokerSettingsAPI,
    updateJokerSettings as updateJokerSettingsAPI,
    createJoker as createJokerAPI,
    updateJoker as updateJokerAPI,
    getJokerSession as getJokerSessionAPI,
    getJokerSessionById as getJokerSessionByIdAPI
} from "../../../helpers/apis";

/**
 * Fetch Joker Settings
 */
export const fetchJokerSettings = createAsyncThunk(
    "jokerSettings/fetch",
    async (_, { rejectWithValue }) => {
        try {
            // First, try to fetch running session
            const runningParams = { status: 'running' };
            const runningRes = await getJokerSessionAPI(runningParams);

            // Map the response to match what the state expects. 
            // The API returns { data: { sessions: [...] } }
            // If a running session exists, return it.
            const runningSession = runningRes.data && runningRes.data.sessions && runningRes.data.sessions.length > 0
                ? runningRes.data.sessions[0]
                : null;

            if (runningSession) {
                // Return in format state expects:
                return {
                    jokerEnabled: true,
                    totalJokerRounds: runningSession.totalJokerRounds,
                    startTime: runningSession.startTime,
                    endTime: runningSession.endTime,
                    multiplier: runningSession.multiplier,
                    currentSession: runningSession
                };
            }

            // If no running session, fetch pending sessions and get the latest one
            const pendingParams = { status: 'pending' };
            const pendingRes = await getJokerSessionAPI(pendingParams);

            // Get the latest pending session (first one, assuming API returns sorted by date)
            const pendingSessions = pendingRes.data && pendingRes.data.sessions ? pendingRes.data.sessions : [];

            if (pendingSessions.length > 0) {
                // Sort by startTime descending to get the latest pending session
                const sortedPending = [...pendingSessions].sort((a, b) => {
                    const dateA = new Date(a.startTime || a.createdAt || 0);
                    const dateB = new Date(b.startTime || b.createdAt || 0);
                    return dateB - dateA; // Descending order (latest first)
                });

                const latestPending = sortedPending[0];

                return {
                    jokerEnabled: false,
                    totalJokerRounds: latestPending.totalJokerRounds,
                    startTime: latestPending.startTime,
                    endTime: latestPending.endTime,
                    multiplier: latestPending.multiplier,
                    currentSession: latestPending
                };
            }

            return { jokerEnabled: false };

        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch joker settings"
            );
        }
    }
);

/**
 * Fetch Joker Session (Active)
 */
export const fetchJokerSession = createAsyncThunk(
    "jokerSettings/fetchSession",
    async (status, { rejectWithValue }) => {
        try {
            // If status is provided (e.g. 'running'), pass it as params
            const params = status ? { status } : {};
            const res = await getJokerSessionAPI(params);

            // Extract the first session from the sessions array
            const session = res.data && res.data.sessions && res.data.sessions.length > 0
                ? res.data.sessions[0]
                : null;

            return session;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch joker session"
            );
        }
    }
);

/**
 * Fetch Joker Session Details by ID
 */
export const fetchJokerSessionDetails = createAsyncThunk(
    "jokerSettings/fetchSessionDetails",
    async (jokerId, { rejectWithValue }) => {
        try {
            const res = await getJokerSessionByIdAPI(jokerId);
            // API returns { statusCode, status, message, data }
            // The api_helper returns the full response object
            // Extract the data property which contains the actual session details
            if (res && res.data) {
                return res.data;
            }
            // Fallback: if structure is different, return the whole response
            return res;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Failed to fetch joker session details"
            );
        }
    }
);

/**
 * Create or Update Joker Settings (Admin)
 * If jokerId is provided, it's an update (PUT), otherwise it's a create (POST)
 */
export const saveJokerSettings = createAsyncThunk(
    "jokerSettings/save",
    async (payload, { rejectWithValue }) => {
        try {
            const { jokerId, ...jokerData } = payload;

            let res;
            if (jokerId) {
                // Update existing joker (PUT /api/joker/:id)
                // Backend expects: totalJokerRounds, startTime, endTime, multiplier, status
                const apiData = {};

                if (jokerData.totalJokerRounds !== undefined) {
                    apiData.totalJokerRounds = Number(jokerData.totalJokerRounds);
                }
                if (jokerData.startTime !== undefined) {
                    apiData.startTime = jokerData.startTime;
                }
                if (jokerData.endTime !== undefined) {
                    apiData.endTime = jokerData.endTime;
                }
                if (jokerData.multiplier !== undefined) {
                    apiData.multiplier = Number(jokerData.multiplier);
                }
                if (jokerData.status !== undefined) {
                    apiData.status = jokerData.status;
                }

                res = await updateJokerAPI(jokerId, apiData);
            } else {
                // Create new joker (POST)
                // Map form fields to API fields for creation
                const apiData = {
                    jokerNumber: jokerData.totalJokerRounds || jokerData.jokerNumber,
                    startTime: jokerData.startTime,
                    endTime: jokerData.endTime,
                    multiplier: jokerData.multiplier
                };
                res = await createJokerAPI(apiData);
            }

            return { ...res.data, jokerId: res.data?.id || jokerId };
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || error.message || "Failed to save joker settings"
            );
        }
    }
);
